import {useQueryClient} from '@tanstack/react-query';
import axios from 'axios';
import ClipboardJS from 'clipboard';
import cookie from 'cookie';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';
import {useCallback, useEffect, useState} from 'react';
import {ColorMode} from '../colorMode/ColorMode.enum';
import {getEnv} from '../common/env/env';
import {ServerEnv} from '../common/env/ServerEnv.enum';
import {requestContext} from '../common/requestContext';
import {useLongPress} from '../common/shared/hooks/useLongPress';
import {isTrue} from '../common/shared/utils/isTrue';
import {twMerge} from '../common/twMerge';
import {useColorMode} from '../common/useColorMode';
import {useIsDarkMode} from '../common/useIsDarkMode.hook';
import useTypedDispatch from '../common/useTypedDispatch';
import useTypedSelector from '../common/useTypedSelector';
import {Button} from '../design-system/atoms/Button/Button';
import {ButtonSize} from '../design-system/atoms/Button/Button.enum';
import {ButtonProps} from '../design-system/atoms/Button/ButtonProps.interface';
import {Text} from '../design-system/atoms/Text/Text';
import {addError} from '../errors/ErrorSlice';
import {thunkSerializeError} from '../errors/thunkSerializeError.function';
import {
  ContentHomeOverviewQuery,
  Maybe,
  SaleState,
} from '../graphql/generated/types';
import {debuggingFeature} from '../logging/debuggingFeature.variable';
import {showToast} from '../toast/toastHandler';
import {setUser} from '../user/UserSlice';
import {useLogin} from '../user/useLogin.hook';
import {useContentHomeOverviewQuery} from '../wrapper/contentWrapper';
import {DeveloperFeatureOverrideModal} from './DeveloperFeatureOverrideModal';

const DevTools = dynamic(() => import('../state/DevTools') as any, {
  ssr: false,
});

import {useFeatures} from '@kakaoent/feature-flag';
import {setIsDevBlockHidden} from '../common/DeveloperSlice';
import {LOCAL_STORAGE_BUY_TICKET_FLOW_DEBUG} from '../domain/buy/ticket/constant';
import {xstateInspector} from '../domain/buy/ticket/machine/buyTicketMachine';
import {CountryCode} from '../domain/signup/type';
import {CACHE_ONLY_CONFIG, ONLY_DATA_CONFIG} from '../reactQuery/constant';
import {
  COOKIE_KEY_BYPASS_SELF_AUTH,
  COOKIE_KEY_FORCE_KAKAOBANK_CERT,
  COOKIE_KEY_MANUAL_GEO_IP,
} from './constant';

export function DeveloperBlock() {
  const queryClient = useQueryClient();
  const features = useFeatures();
  const [showDeveloperMenu, setShowDeveloperMenu] = useState(false);
  const [showFeatureOverrideModal, setShowFeatureOverrideModal] =
    useState(false);
  const dispatch = useTypedDispatch();
  const [showReduxDevtools, setShowReduxDevtools] = useState(false);
  const handleShowDeveloperMenu = useCallback(() => {
    getEnv().envName !== ServerEnv.Real && setShowDeveloperMenu(s => !s);
  }, []);
  const handleHideDeveloperButton = useCallback(() => {
    dispatch(setIsDevBlockHidden(true));
  }, [dispatch]);
  const isDevBlockHidden = useTypedSelector(
    state => state.developer.isDevBlockHidden
  );
  const longPressEvent = useLongPress({
    onLongPress: handleHideDeveloperButton,
    shouldPreventDefault: false,
    delayMs: 1000,
  });
  const isMobile = requestContext.requestInfo.display.isMobile;
  const {toggleColorMode} = useColorMode();
  const isDarkMode = useIsDarkMode();
  const router = useRouter();
  const [contentHomeSeriesId, setContentHomeSeriesId] = useState<number>();
  const contentHome = useContentHomeOverviewQuery(
    {
      seriesId: contentHomeSeriesId as number,
    },
    {...ONLY_DATA_CONFIG, ...CACHE_ONLY_CONFIG}
  ).data?.contentHomeOverview;

  const [debuggingFeatureState, setDebuggingFeatureState] =
    useState(debuggingFeature);
  useEffect(() => {
    debuggingFeature.ignoreDefaultEvent =
      debuggingFeatureState.ignoreDefaultEvent;
    debuggingFeature.consoleLog = debuggingFeatureState.consoleLog;
  }, [debuggingFeatureState]);
  useEffect(() => {
    if (!router.pathname.startsWith('/content/[seriesId]')) {
      setContentHomeSeriesId(undefined);
      return;
    }
    setContentHomeSeriesId(Number(router.query['seriesId']));
  }, [router.query, router.pathname]);

  const [viewerProductId, setViewerProductId] = useState<number>();
  useEffect(() => {
    if (!router.pathname.startsWith('/content/[seriesId]/viewer/[productId]')) {
      setViewerProductId(undefined);
      return;
    }
    setViewerProductId(Number(router.query['productId']));
  }, [router.query, router.pathname]);

  const [bypassSelfAuth, setBypassSelfAuth] = useState<boolean>(false);
  useEffect(() => {
    setBypassSelfAuth(
      isTrue(cookie.parse(document.cookie)[COOKIE_KEY_BYPASS_SELF_AUTH])
    );
  }, []);
  const toggleBypassSelfAuth = useCallback(() => {
    const changeToBypassSelfAuth = !bypassSelfAuth;
    document.cookie = `${COOKIE_KEY_BYPASS_SELF_AUTH}=${changeToBypassSelfAuth}; domain=.kakao.com; path=/;`;
    setBypassSelfAuth(changeToBypassSelfAuth);
  }, [bypassSelfAuth]);

  const [forceKakaobankCert, setForceKakaobankCert] = useState<boolean>(false);
  useEffect(() => {
    setForceKakaobankCert(
      isTrue(cookie.parse(document.cookie)[COOKIE_KEY_FORCE_KAKAOBANK_CERT])
    );
  }, []);
  const toggleForceKakaobankCert = useCallback(() => {
    const changeToForceKakaobankCert = !forceKakaobankCert;
    document.cookie = `${COOKIE_KEY_FORCE_KAKAOBANK_CERT}=${changeToForceKakaobankCert}; domain=.kakao.com; path=/;`;
    setForceKakaobankCert(changeToForceKakaobankCert);
  }, [forceKakaobankCert]);

  const {isLogin, user} = useLogin();

  const fetchInvalidToken = useCallback(
    async (
      invalidToken:
        | 'stoken'
        | 'userId'
        | 'kakaoAccessToken'
        | 'kakaoRefreshToken'
        | 'appUserId'
    ) => {
      const response = await axios.put(
        '/api/invalid-token',
        {
          [invalidToken]: true,
        },
        {withCredentials: true}
      );
      alert('응답 ' + response.status);
    },
    []
  );
  const [manualGeoIp, setManualGeoIp] = useState<Maybe<string>>();
  useEffect(() => {
    setManualGeoIp(cookie.parse(document.cookie)[COOKIE_KEY_MANUAL_GEO_IP]);
  }, []);
  const handleClickManualGeoIp = useCallback(() => {
    const options = [undefined, 'KR', 'US', 'TH'];
    const currentIndex = manualGeoIp ? options.indexOf(manualGeoIp) : 0;
    const nextOption = options[(currentIndex + 1) % options.length];

    document.cookie = `${COOKIE_KEY_MANUAL_GEO_IP}=${nextOption}; path=/; ${nextOption ? '' : 'Max-Age=0;'}`;
    setManualGeoIp(nextOption);
    if (nextOption) {
      requestContext.requestInfo.appInit.geoIp = nextOption as CountryCode;
    } else {
      // 새로고침하여 appInit API의 결과로 세팅해줍니다.
      window.location.reload();
    }
  }, [manualGeoIp]);

  const DeveloperButton = ({children, ...props}: ButtonProps) => (
    <Button size={isMobile ? ButtonSize.Small : ButtonSize.Medium} {...props}>
      {children}
    </Button>
  );

  return (
    <>
      <div
        className={twMerge(
          'fixed bottom-0 right-0 z-100000 m-10px h-40px w-40px cursor-pointer rounded-full',
          contentHome && 'mb-70px',
          (showDeveloperMenu || isDevBlockHidden) && 'hidden'
        )}
        style={{
          backgroundImage: "url('/public/images/dev-button.png')",
          backgroundSize: 'cover',
        }}
        {...longPressEvent}
        onClick={handleShowDeveloperMenu}
      />
      {showDeveloperMenu && (
        <ul className="fixed bottom-1px right-1px z-100000 flex flex-col items-end">
          <li>
            <DeveloperButton
              onClick={() => {
                setShowDeveloperMenu(s => !s);
              }}
            >
              닫기
            </DeveloperButton>
          </li>
          <li
            className={twMerge(
              'bg-theme-solid-20 text-theme-solid-100',
              !isMobile && 'text-center',
              isMobile && 'font-small1'
            )}
          >
            <Text>버전 : {getEnv().version}</Text>
            <br />
            <Text>구동환경 : {getEnv().envName}</Text>
            <br />
            <Text>구동시간 : {getEnv().uptime}</Text>
            <br />
            <Text
              className="cursor-pointer"
              onClick={() => {
                ClipboardJS.copy(requestContext.requestInfo.deviceId);
                showToast({
                  message: '클립보드에 디바이스ID를 복사했습니다',
                  isMobile,
                  delayMs: 5000,
                });
              }}
            >
              DeviceId
              <br />
              {requestContext?.requestInfo?.deviceId}
            </Text>
            <br />
            <Text>
              IdentifierId(브라우저 정보 베이스 ID)
              <br />
              {requestContext?.requestInfo?.identifierId}
            </Text>
            <br />
            <Text>
              본인인증타입 : {requestContext.requestInfo.appInit.certType}
            </Text>
            <br />
            <Text>GeoIp : {requestContext.requestInfo.appInit.geoIp}</Text>
            <br />
            <Text>CountryCode : {requestContext.requestInfo.countryCode}</Text>
          </li>
          <li>
            <DeveloperButton onClick={toggleBypassSelfAuth}>
              본인인증 테스트 모드 : {bypassSelfAuth ? 'on' : 'off'}
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton onClick={toggleForceKakaobankCert}>
              카뱅 본인인증 강제 설정 : {forceKakaobankCert ? 'on' : 'off'}
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton
              onClick={() => {
                setShowFeatureOverrideModal(true);
              }}
            >
              임시 피쳐 제어
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton
              onClick={() => {
                localStorage.setItem(
                  LOCAL_STORAGE_BUY_TICKET_FLOW_DEBUG,
                  String(
                    !isTrue(
                      localStorage.getItem(LOCAL_STORAGE_BUY_TICKET_FLOW_DEBUG)
                    )
                  )
                );
                window.location.reload();
              }}
            >
              구매플로우 콘솔 디버깅 :{' '}
              {isTrue(localStorage.getItem(LOCAL_STORAGE_BUY_TICKET_FLOW_DEBUG))
                ? 'on'
                : 'off'}
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton onClick={() => xstateInspector?.start()}>
              구매플로우 Inspect
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton onClick={toggleColorMode}>
              컬러모드: {isDarkMode ? ColorMode.Dark : ColorMode.Light}
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton
              onClick={() => {
                setDebuggingFeatureState({
                  ...debuggingFeature,
                  ignoreDefaultEvent: !debuggingFeatureState.ignoreDefaultEvent,
                });
              }}
            >
              이벤트로깅 클릭 이동 막기:{' '}
              {`${debuggingFeatureState.ignoreDefaultEvent}`}
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton
              onClick={() => {
                setDebuggingFeatureState({
                  ...debuggingFeature,
                  consoleLog: !debuggingFeatureState.consoleLog,
                });
              }}
            >
              이벤트로깅 콘솔 로그: {`${debuggingFeatureState.consoleLog}`}
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton
              onClick={() => {
                fetchInvalidToken('stoken');
              }}
            >
              stoken 만료
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton
              onClick={() => {
                fetchInvalidToken('kakaoAccessToken');
              }}
            >
              엑세스토큰 만료
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton
              onClick={() => {
                fetchInvalidToken('kakaoRefreshToken');
              }}
            >
              리프레쉬토큰 만료
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton
              onClick={() => {
                fetchInvalidToken('appUserId');
              }}
            >
              잘못된 appUserId
            </DeveloperButton>
          </li>
          <li>
            <DeveloperButton
              onClick={() => {
                dispatch(
                  addError({
                    error: thunkSerializeError(new Error('에러 테스트')),
                  })
                );
              }}
            >
              에러 발생
            </DeveloperButton>
          </li>
          {!window.__REDUX_DEVTOOLS_EXTENSION__ && (
            <li>
              <DeveloperButton
                onClick={() => {
                  setShowReduxDevtools(!showReduxDevtools);
                }}
              >
                Redux Devtools 사용하기
              </DeveloperButton>
            </li>
          )}
          <li>
            <DeveloperButton
              onClick={() => {
                window.localStorage?.setItem(
                  'STOP_BANNER_AUTOPLAY_FOR_TEST',
                  String(
                    !isTrue(
                      window.localStorage?.getItem(
                        'STOP_BANNER_AUTOPLAY_FOR_TEST'
                      )
                    )
                  )
                );
                window.location.reload();
              }}
            >
              배너 자동 재생{' '}
              {isTrue(
                window.localStorage?.getItem('STOP_BANNER_AUTOPLAY_FOR_TEST')
              )
                ? 'Off'
                : 'On'}
            </DeveloperButton>
          </li>
          {(router.pathname === '/account/register' || isLogin) && (
            <>
              <li>
                <DeveloperButton
                  onClick={() => {
                    const ageChain = [0, 13, 14, 18, 19, 20];
                    const nextAgeIndex = ageChain.findIndex(
                      age => age > (user?.age || 0)
                    );
                    const nextAge =
                      ageChain[nextAgeIndex > -1 ? nextAgeIndex : 0];
                    dispatch(setUser({...user, age: nextAge}));
                  }}
                >
                  클라이언트단 연령 변경(현재 {user?.age}세)
                </DeveloperButton>
              </li>
            </>
          )}
          {contentHome && (
            <li>
              <DeveloperButton
                onClick={() => {
                  const saleState =
                    contentHome?.content?.saleState || SaleState.Unknown;
                  if (!contentHomeSeriesId) {
                    return;
                  }
                  queryClient.setQueryData<ContentHomeOverviewQuery>(
                    useContentHomeOverviewQuery.getKey({
                      seriesId: contentHomeSeriesId,
                    }),
                    // @ts-expect-error
                    prevData => {
                      if (!prevData) {
                        return prevData;
                      }
                      return {
                        ...prevData,
                        contentHomeOverview: {
                          ...prevData.contentHomeOverview,
                          content: prevData.contentHomeOverview?.content
                            ? {
                                ...prevData.contentHomeOverview.content,
                                saleState:
                                  saleState === SaleState.Ing
                                    ? SaleState.Stop
                                    : SaleState.Ing,
                              }
                            : undefined,
                        },
                      };
                    }
                  );
                }}
              >
                판매상태 토글(현재 {contentHome?.content?.saleState})
              </DeveloperButton>
            </li>
          )}
          <li>
            <DeveloperButton onClick={handleClickManualGeoIp}>
              geo Ip 수동세팅: {manualGeoIp ?? 'off'}
            </DeveloperButton>
          </li>
        </ul>
      )}
      {showReduxDevtools && <DevTools />}
      {showFeatureOverrideModal && (
        <DeveloperFeatureOverrideModal
          features={features}
          onClose={() => {
            setShowFeatureOverrideModal(false);
          }}
        />
      )}
    </>
  );
}
